import type { GroupRoundGame } from '@/lib/db/types';
import { buildSkinsPlayers } from './helpers';
import { teamBestNet } from '@/lib/games/formats/team-formats';
import { appliesMoney, getLegStakes, getMoneyAmount, getScoringMode, getStakeConfig } from './stakes-helpers';
import type { GameInstanceConfig, MatchPress, PressSegment, PressSide, RoundContext } from './types';
import { isNassauFormat, normalizeNassauMatches } from './nassau-matches';

export const MATCH_PRESS_FORMAT_IDS = new Set([
  'nassau',
  'team-nassau',
  'match-play',
  'four-ball',
  'team-match-play',
]);

export function formatSupportsPresses(formatId: string): boolean {
  return MATCH_PRESS_FORMAT_IDS.has(formatId);
}

export function parsePresses(config: Record<string, unknown> | undefined): MatchPress[] {
  const raw = config?.presses;
  if (!Array.isArray(raw)) return [];
  return raw.filter((p): p is MatchPress => Boolean(p && typeof p === 'object' && typeof (p as MatchPress).id === 'string'));
}

export function segmentRange(segment: PressSegment, holeCount: number): { startIdx: number; endIdx: number } | null {
  if (holeCount <= 0) return null;
  if (holeCount >= 18) {
    if (segment === 'front') return { startIdx: 0, endIdx: 8 };
    if (segment === 'back') return { startIdx: 9, endIdx: 17 };
    return { startIdx: 0, endIdx: 17 };
  }
  if (segment === 'back') return null;
  return { startIdx: 0, endIdx: holeCount - 1 };
}

export function segmentsForFormat(formatId: string, holeCount: number): PressSegment[] {
  if (formatId === 'nassau' || formatId === 'team-nassau') {
    if (holeCount >= 18) return ['front', 'back', 'total'];
    return ['front', 'total'];
  }
  return ['total'];
}

export interface MatchSides {
  sideA: string[];
  sideB: string[];
}

export function resolveMatchSides(
  formatId: string,
  ctx: RoundContext,
  config: GameInstanceConfig,
  matchId?: string,
): MatchSides | null {
  if (isNassauFormat(formatId)) {
    const stake = getStakeConfig(ctx.stakes, formatId);
    const matches = normalizeNassauMatches(formatId, config, ctx, getLegStakes(stake));
    const match = matchId
      ? matches.find((m) => m.id === matchId)
      : matches[0];
    if (!match) return null;
    return { sideA: match.sideA, sideB: match.sideB };
  }
  if (formatId === 'four-ball' || formatId === 'team-match-play') {
    const teams = ctx.teams;
    if (!teams || teams.length < 2) return null;
    return { sideA: teams[0], sideB: teams[1] };
  }
  const oppKey = config.opponentKey;
  if (!oppKey) return null;
  const aKey = ctx.players.find((p) => p.playerKey !== oppKey)?.playerKey;
  if (!aKey || aKey === oppKey) return null;
  return { sideA: [aKey], sideB: [oppKey] };
}

function useNet(ctx: RoundContext, formatId: string): boolean {
  const stake = getStakeConfig(ctx.stakes, formatId);
  return getScoringMode(stake) !== 'gross';
}

function holeWinner(
  ctx: RoundContext,
  formatId: string,
  sides: MatchSides,
  holeIdx: number,
): PressSide | null {
  const isTeam = formatId === 'four-ball' || formatId === 'team-match-play'
    || sides.sideA.length > 1 || sides.sideB.length > 1;
  if (isTeam) {
    const a = teamBestNet(sides.sideA, holeIdx, ctx.players, ctx.holes, ctx.scores, 'fourball');
    const b = teamBestNet(sides.sideB, holeIdx, ctx.players, ctx.holes, ctx.scores, 'fourball');
    if (a == null || b == null) return null;
    if (a < b) return 'A';
    if (b < a) return 'B';
    return null;
  }
  const built = buildSkinsPlayers(ctx, formatId, [...sides.sideA, ...sides.sideB]);
  const aP = built.find((p) => p.id === sides.sideA[0]);
  const bP = built.find((p) => p.id === sides.sideB[0]);
  if (!aP || !bP) return null;
  const net = useNet(ctx, formatId);
  const a = net ? aP.net[holeIdx] : aP.gross[holeIdx];
  const b = net ? bP.net[holeIdx] : bP.gross[holeIdx];
  if (a == null || b == null) return null;
  if (a < b) return 'A';
  if (b < a) return 'B';
  return null;
}

export function holesWonInRange(
  ctx: RoundContext,
  formatId: string,
  sides: MatchSides,
  startIdx: number,
  endIdx: number,
): { aWins: number; bWins: number } {
  let aWins = 0;
  let bWins = 0;
  for (let hi = startIdx; hi <= endIdx; hi += 1) {
    const w = holeWinner(ctx, formatId, sides, hi);
    if (w === 'A') aWins += 1;
    else if (w === 'B') bWins += 1;
  }
  return { aWins, bWins };
}

export function stakeCentsForPress(
  formatId: string,
  segment: PressSegment,
  ctx: RoundContext,
  matchStakes?: { front: number; back: number; total: number },
): number {
  const stake = getStakeConfig(ctx.stakes, formatId);
  if (formatId === 'nassau' || formatId === 'team-nassau') {
    const legs = matchStakes ?? getLegStakes(stake);
    const leg = segment === 'front' ? legs.front : segment === 'back' ? legs.back : legs.total;
    return Math.round(leg * 100);
  }
  return Math.round(getMoneyAmount(stake) * 100);
}

export function parentBetId(formatId: string, segment: PressSegment, matchId?: string): string {
  if (matchId) return `${matchId}:${segment}`;
  return `${formatId}:${segment}`;
}

export function sideForPlayer(sides: MatchSides, playerKey: string): PressSide | null {
  if (sides.sideA.includes(playerKey)) return 'A';
  if (sides.sideB.includes(playerKey)) return 'B';
  return null;
}

export interface PressEligibility {
  canPress: boolean;
  segment: PressSegment | null;
  initiatingSide: PressSide | null;
  startHole: number;
  endHole: number;
  stakeCents: number;
  parentBetId: string;
  matchId?: string;
  reason?: string;
}

const MAX_PRESSES_PER_SEGMENT = 3;

export function evaluatePressEligibility(opts: {
  formatId: string;
  game: GroupRoundGame;
  ctx: RoundContext;
  config: GameInstanceConfig;
  currentHoleIdx: number;
  playerKey: string;
}): PressEligibility {
  return evaluateAllPressEligibilities(opts)[0] ?? {
    canPress: false,
    segment: null,
    initiatingSide: null,
    startHole: 0,
    endHole: 0,
    stakeCents: 0,
    parentBetId: '',
    reason: 'Not eligible to press',
  };
}

export function evaluateAllPressEligibilities(opts: {
  formatId: string;
  game: GroupRoundGame;
  ctx: RoundContext;
  config: GameInstanceConfig;
  currentHoleIdx: number;
  playerKey: string;
}): PressEligibility[] {
  const { formatId, game, ctx, config, currentHoleIdx, playerKey } = opts;
  const stake = getStakeConfig(ctx.stakes, formatId);
  if (!formatSupportsPresses(formatId) || !appliesMoney(stake)) {
    return [];
  }

  const nassauMatches = isNassauFormat(formatId)
    ? normalizeNassauMatches(formatId, config, ctx, getLegStakes(stake))
    : null;
  const matchCandidates = nassauMatches?.length
    ? nassauMatches.filter((m) => m.sideA.includes(playerKey) || m.sideB.includes(playerKey))
    : [null];

  const results: PressEligibility[] = [];

  for (const match of matchCandidates) {
    const sides = match
      ? { sideA: match.sideA, sideB: match.sideB }
      : resolveMatchSides(formatId, ctx, config);
    if (!sides) continue;
    const mySide = sideForPlayer(sides, playerKey);
    if (!mySide) continue;

    const holeCount = ctx.holes.length;
    const segments = segmentsForFormat(formatId, holeCount);
    const presses = parsePresses((game.config || {}) as Record<string, unknown>);

    for (const segment of segments) {
      const range = segmentRange(segment, holeCount);
      if (!range) continue;
      if (currentHoleIdx > range.endIdx) continue;
      if (currentHoleIdx >= range.endIdx) continue;

      const openInSegment = presses.filter((p) => {
        if (p.segment !== segment || p.status !== 'open') return false;
        if (match?.id && p.matchId !== match.id) return false;
        return true;
      }).length;
      if (openInSegment >= MAX_PRESSES_PER_SEGMENT) continue;

      const throughIdx = Math.max(range.startIdx, currentHoleIdx - 1);
      if (throughIdx < range.startIdx) continue;

      const { aWins, bWins } = holesWonInRange(ctx, formatId, sides, range.startIdx, throughIdx);
      const losingSide: PressSide | null = aWins < bWins ? 'A' : bWins < aWins ? 'B' : null;
      if (!losingSide || losingSide !== mySide) continue;

      const startHole = currentHoleIdx + 1;
      const endHole = range.endIdx + 1;
      const stakeCents = stakeCentsForPress(formatId, segment, ctx, match?.stakes);
      const pid = parentBetId(formatId, segment, match?.id);

      results.push({
        canPress: true,
        segment,
        initiatingSide: mySide,
        startHole,
        endHole,
        stakeCents,
        parentBetId: pid,
        matchId: match?.id,
      });
    }
  }

  return results;
}

function distributePressWinnings(sides: MatchSides, winner: PressSide, stakeCents: number): Record<string, number> {
  const net: Record<string, number> = {};
  [...sides.sideA, ...sides.sideB].forEach((k) => { net[k] = 0; });
  const winKeys = winner === 'A' ? sides.sideA : sides.sideB;
  const loseKeys = winner === 'A' ? sides.sideB : sides.sideA;
  const winEach = stakeCents / winKeys.length;
  const loseEach = stakeCents / loseKeys.length;
  winKeys.forEach((k) => { net[k] = winEach; });
  loseKeys.forEach((k) => { net[k] = -loseEach; });
  return net;
}

export function settlePress(
  press: MatchPress,
  ctx: RoundContext,
  formatId: string,
): { press: MatchPress; netOwed: Record<string, number> } {
  const sides: MatchSides = { sideA: press.sideAPlayerKeys, sideB: press.sideBPlayerKeys };
  const startIdx = press.startHole - 1;
  const endIdx = press.endHole - 1;
  const { aWins, bWins } = holesWonInRange(ctx, formatId, sides, startIdx, endIdx);

  let winner: PressSide | null = null;
  if (aWins > bWins) winner = 'A';
  else if (bWins > aWins) winner = 'B';

  const netOwed = winner ? distributePressWinnings(sides, winner, press.stakeCents) : {};
  let status: MatchPress['status'] = 'tied';
  if (winner) {
    status = winner === press.initiatingSide ? 'won' : 'lost';
  }
  return { press: { ...press, status }, netOwed };
}

export function isPressRangeComplete(
  press: MatchPress,
  ctx: RoundContext,
  sides: MatchSides,
): boolean {
  const startIdx = press.startHole - 1;
  const endIdx = press.endHole - 1;
  for (let hi = startIdx; hi <= endIdx; hi += 1) {
    const allKeys = [...sides.sideA, ...sides.sideB];
    if (!allKeys.every((k) => ctx.scores[k]?.[hi] != null)) return false;
  }
  return true;
}

export interface PressSettlementLine {
  press: MatchPress;
  label: string;
  netOwed: Record<string, number>;
}

export function settleAllPresses(
  game: GroupRoundGame,
  ctx: RoundContext,
  config: GameInstanceConfig,
): { netOwed: Record<string, number>; lines: PressSettlementLine[]; presses: MatchPress[] } {
  const formatId = game.format_id;
  if (!formatSupportsPresses(formatId)) {
    return { netOwed: {}, lines: [], presses: [] };
  }
  const sides = resolveMatchSides(formatId, ctx, config);
  if (!sides) return { netOwed: {}, lines: [], presses: [] };

  const presses = parsePresses((game.config || {}) as Record<string, unknown>);
  const netOwed: Record<string, number> = {};
  const lines: PressSettlementLine[] = [];
  const updatedPresses = presses.map((press) => {
    if (press.status !== 'open') return press;
    if (!isPressRangeComplete(press, ctx, sides)) return press;
    const { press: settled, netOwed: delta } = settlePress(press, ctx, formatId);
    Object.entries(delta).forEach(([k, v]) => { netOwed[k] = (netOwed[k] || 0) + v; });
    lines.push({
      press: settled,
      label: `Press ${press.segment} holes ${press.startHole}–${press.endHole}`,
      netOwed: delta,
    });
    return settled;
  });

  return { netOwed, lines, presses: updatedPresses };
}

/** Returns game configs whose open presses should be persisted with settled status. */
export function pressConfigUpdates(
  games: GroupRoundGame[],
  ctx: RoundContext,
): Array<{ formatId: string; config: Record<string, unknown> }> {
  const updates: Array<{ formatId: string; config: Record<string, unknown> }> = [];
  games.filter((g) => g.enabled && formatSupportsPresses(g.format_id)).forEach((g) => {
    const rawConfig = (g.config || {}) as Record<string, unknown>;
    const config = rawConfig as GameInstanceConfig;
    const before = parsePresses(rawConfig);
    const { presses: after } = settleAllPresses(g, ctx, config);
    const changed = before.length !== after.length
      || before.some((p, i) => p.status !== after[i]?.status);
    if (changed) {
      updates.push({
        formatId: g.format_id,
        config: { ...rawConfig, presses: after },
      });
    }
  });
  return updates;
}

export function createPress(opts: {
  formatId: string;
  segment: PressSegment;
  startHole: number;
  endHole: number;
  stakeCents: number;
  initiatingSide: PressSide;
  parentBetId: string;
  sides: MatchSides;
  matchId?: string;
}): MatchPress {
  return {
    id: `press-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    parentBetId: opts.parentBetId,
    segment: opts.segment,
    startHole: opts.startHole,
    endHole: opts.endHole,
    stakeCents: opts.stakeCents,
    initiatingSide: opts.initiatingSide,
    sideAPlayerKeys: opts.sides.sideA,
    sideBPlayerKeys: opts.sides.sideB,
    status: 'open',
    createdAt: new Date().toISOString(),
    ...(opts.matchId ? { matchId: opts.matchId } : {}),
  };
}
